import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { apiService } from '../services/api';

const ApproveSubmissions = ({ adminUserId }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submissionType, setSubmissionType] = useState('all');
  const [alert, setAlert] = useState(null);
  const [submissionDetailOpen, setSubmissionDetailOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      
      const propertiesRes = await apiService.getPendingProperties();
      const properties = propertiesRes.success ? propertiesRes.data || [] : [];
      
      const servicesRes = await apiService.getPendingServices();
      const services = servicesRes.success ? servicesRes.data || [] : [];
      
      const allSubmissions = [
        ...properties.map(p => ({ ...p, submissionType: 'property' })),
        ...services.map(s => ({ ...s, submissionType: 'service' }))
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setSubmissions(allSubmissions);
    } catch (error) {
      showAlert('❌ Error fetching submissions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, severity = 'success') => {
    setAlert({ message, severity });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleApprove = async (submission) => {
    try {
      setLoading(true);
      let response;
      
      if (submission.submissionType === 'property') {
        response = await apiService.approveProperty(submission.id, adminUserId);
      } else {
        response = await apiService.approveService(submission.id, adminUserId);
      }
      
      if (response.success) {
        setSubmissions(submissions.filter(s => s.id !== submission.id));
        showAlert(`✅ ${submission.submissionType === 'property' ? 'Property' : 'Service'} approved successfully!`);
        setSubmissionDetailOpen(false);
      } else {
        showAlert('❌ Error: ' + (response.message || 'Failed to approve'), 'error');
      }
    } catch (error) {
      showAlert('❌ Error: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      showAlert('❌ Please provide a rejection reason', 'error');
      return;
    }

    try {
      setLoading(true);
      let response;
      
      if (selectedSubmission.submissionType === 'property') {
        response = await apiService.rejectProperty(selectedSubmission.id, adminUserId, rejectReason);
      } else {
        response = await apiService.rejectService(selectedSubmission.id, adminUserId, rejectReason);
      }
      
      if (response.success) {
        setSubmissions(submissions.filter(s => s.id !== selectedSubmission.id));
        showAlert(`✅ ${selectedSubmission.submissionType === 'property' ? 'Property' : 'Service'} rejected successfully!`);
        setRejectDialogOpen(false);
        setRejectReason('');
        setSubmissionDetailOpen(false);
      } else {
        showAlert('❌ Error: ' + (response.message || 'Failed to reject'), 'error');
      }
    } catch (error) {
      showAlert('❌ Error: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredSubmissions = () => {
    if (submissionType === 'properties') {
      return submissions.filter(s => s.submissionType === 'property');
    } else if (submissionType === 'services') {
      return submissions.filter(s => s.submissionType === 'service');
    }
    return submissions;
  };

  if (loading && submissions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {alert && (
        <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 3 }}>
          {alert.message}
        </Alert>
      )}
      
      <Box sx={{ mb: 3, display: 'flex', gap: 1 }}>
        <Button
          variant={submissionType === 'all' ? 'contained' : 'outlined'}
          onClick={() => setSubmissionType('all')}
          sx={{ transition: 'all 0.3s', '&:hover': { transform: 'translateY(-2px)' } }}
        >
          All ({submissions.length})
        </Button>
        <Button
          variant={submissionType === 'properties' ? 'contained' : 'outlined'}
          onClick={() => setSubmissionType('properties')}
          sx={{ transition: 'all 0.3s', '&:hover': { transform: 'translateY(-2px)' } }}
        >
          Properties ({submissions.filter(s => s.submissionType === 'property').length})
        </Button>
        <Button
          variant={submissionType === 'services' ? 'contained' : 'outlined'}
          onClick={() => setSubmissionType('services')}
          sx={{ transition: 'all 0.3s', '&:hover': { transform: 'translateY(-2px)' } }}
        >
          Services ({submissions.filter(s => s.submissionType === 'service').length})
        </Button>
      </Box>

      {getFilteredSubmissions().length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">No pending submissions to review</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {getFilteredSubmissions().map((submission) => (
            <Grid item xs={12} sm={6} md={4} key={submission.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 4
                  }
                }}
              >
                {submission.images && submission.images.length > 0 && (
                  <CardMedia
                    component="img"
                    height="200"
                    image={submission.images[0].image_url || submission.profileImage}
                    alt={submission.title || submission.name}
                    sx={{ objectFit: 'cover' }}
                  />
                )}
                {submission.image_url && (
                  <CardMedia
                    component="img"
                    height="200"
                    image={submission.image_url}
                    alt={submission.name}
                    sx={{ objectFit: 'cover' }}
                  />
                )}

                <CardContent sx={{ flexGrow: 1 }}>
                  <Chip
                    label={submission.submissionType === 'property' ? '🏠 Property' : '👤 Service'}
                    size="small"
                    color={submission.submissionType === 'property' ? 'primary' : 'secondary'}
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />

                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {submission.title || submission.name}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {submission.submissionType === 'property' ? (
                      <>
                        <strong>📍 {submission.location}</strong><br />
                        <strong>₹{parseFloat(submission.price).toLocaleString('en-IN')}</strong><br />
                        {submission.beds > 0 && `🛏️ ${submission.beds} Beds | `}
                        {submission.baths > 0 && `🚿 ${submission.baths} Baths`}
                      </>
                    ) : (
                      <>
                        <strong>📁 {submission.field}</strong><br />
                        <strong>📞 {submission.phone1}</strong><br />
                        {submission.rate && `💰 ${submission.rate}`}
                      </>
                    )}
                  </Typography>

                  <Box sx={{ bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1, mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {submission.submissionType === 'property' ? submission.ownerName : submission.name}
                    </Typography><br />
                    <Typography variant="caption" color="text.secondary">
                      📞 {submission.submissionType === 'property' ? submission.ownerPhone : submission.phone1}
                    </Typography>
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    Submitted: {new Date(submission.created_at).toLocaleDateString('en-IN')}
                  </Typography>
                </CardContent>

                <CardActions sx={{ gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      setSelectedSubmission(submission);
                      setSubmissionDetailOpen(true);
                    }}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={() => handleApprove(submission)}
                    disabled={loading}
                  >
                    ✅
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    fullWidth
                    onClick={() => {
                      setSelectedSubmission(submission);
                      setRejectDialogOpen(true);
                    }}
                    disabled={loading}
                  >
                    ❌
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Submission Detail Dialog */}
      <Dialog
        open={submissionDetailOpen}
        onClose={() => setSubmissionDetailOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedSubmission && (
          <>
            <DialogTitle sx={{ fontWeight: 600 }}>
              {selectedSubmission.submissionType === 'property' ? '🏠 Property Details' : '👤 Service Details'}
            </DialogTitle>
            <DialogContent sx={{ py: 2 }}>
              {selectedSubmission.images && selectedSubmission.images.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box
                    component="img"
                    src={selectedSubmission.images[0].image_url}
                    alt="Preview"
                    sx={{ width: '100%', borderRadius: 1, maxHeight: 300, objectFit: 'cover', mb: 1 }}
                  />
                </Box>
              )}

              {selectedSubmission.submissionType === 'property' ? (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {selectedSubmission.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {selectedSubmission.description}
                  </Typography>
                  <Box sx={{ bgcolor: '#f0f7ff', p: 1.5, borderRadius: 1, mb: 2 }}>
                    <Typography variant="body2"><strong>📍 Location:</strong> {selectedSubmission.location}</Typography>
                    <Typography variant="body2"><strong>💰 Price:</strong> ₹{parseFloat(selectedSubmission.price).toLocaleString('en-IN')}</Typography>
                    <Typography variant="body2"><strong>📁 Category:</strong> {selectedSubmission.category}</Typography>
                    {selectedSubmission.beds > 0 && <Typography variant="body2"><strong>🛏️ Beds:</strong> {selectedSubmission.beds}</Typography>}
                    {selectedSubmission.baths > 0 && <Typography variant="body2"><strong>🚿 Baths:</strong> {selectedSubmission.baths}</Typography>}
                    {selectedSubmission.area && <Typography variant="body2"><strong>📐 Area:</strong> {selectedSubmission.area}</Typography>}
                  </Box>
                  <Box sx={{ bgcolor: '#f0f7ff', p: 1.5, borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>👤 Owner Information</Typography>
                    <Typography variant="body2"><strong>Name:</strong> {selectedSubmission.ownerName}</Typography>
                    <Typography variant="body2"><strong>Phone:</strong> {selectedSubmission.ownerPhone}</Typography>
                    {selectedSubmission.ownerEmail && <Typography variant="body2"><strong>Email:</strong> {selectedSubmission.ownerEmail}</Typography>}
                  </Box>
                </>
              ) : (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {selectedSubmission.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {selectedSubmission.note}
                  </Typography>
                  <Box sx={{ bgcolor: '#f0f7ff', p: 1.5, borderRadius: 1, mb: 2 }}>
                    <Typography variant="body2"><strong>📁 Service:</strong> {selectedSubmission.field}</Typography>
                    <Typography variant="body2"><strong>📍 Address:</strong> {selectedSubmission.address}</Typography>
                    {selectedSubmission.rate && <Typography variant="body2"><strong>💰 Rate:</strong> {selectedSubmission.rate}</Typography>}
                  </Box>
                  <Box sx={{ bgcolor: '#f0f7ff', p: 1.5, borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>📞 Contact Information</Typography>
                    <Typography variant="body2"><strong>Primary:</strong> {selectedSubmission.phone1}</Typography>
                    {selectedSubmission.phone2 && <Typography variant="body2"><strong>Secondary:</strong> {selectedSubmission.phone2}</Typography>}
                    {selectedSubmission.email && <Typography variant="body2"><strong>Email:</strong> {selectedSubmission.email}</Typography>}
                  </Box>
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button onClick={() => setSubmissionDetailOpen(false)} color="primary">
                Close
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleApprove(selectedSubmission)}
                disabled={loading}
              >
                ✅ Approve
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => setRejectDialogOpen(true)}
                disabled={loading}
              >
                ❌ Reject
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => {
          setRejectDialogOpen(false);
          setRejectReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#d32f2f' }}>
          ❌ Reject Submission
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for rejection:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="e.g., Missing required information, Inappropriate content, Poor image quality, etc."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setRejectDialogOpen(false);
              setRejectReason('');
            }}
            color="primary"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={loading || !rejectReason.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {loading ? 'Rejecting...' : 'Reject Submission'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApproveSubmissions;